import { useState, useRef, useEffect } from 'react';
import { Axis } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import { LinePath, Bar, AreaClosed } from '@visx/shape';
import { Threshold } from '@visx/threshold';
import { Text } from '@visx/text';
import { extent } from 'd3-array';
import { curveMonotoneX } from 'd3-shape';
import { localPoint } from '@visx/event';

import { toTruncated } from '@/utilities/formatting';

import type { NarrativeValuatorCompany } from '../../hooks/narrativeValuatorCompanyFragment';
import useResizeObserver from 'use-resize-observer';
import { TimeseriesTooltips } from './components/TimeseriesTooltips';
import {
  LINE_PATH_STROKE,
  TICK_FONT_SIZE,
  X_AXIS_OFFSET_LEFT,
  X_AXIS_OFFSET_RIGHT,
  X_AXIS_PADDING_LEFT,
  X_AXIS_PADDING_RIGHT,
  Y_AXIS_OFFSET_BOTTOM,
  Y_AXIS_OFFSET_RIGHT,
  Y_AXIS_OFFSET_TOP,
} from './lib';
import { useXAxisConfig } from './hooks/useXAxisConfig';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getReportingCurrencyISO } from '@/utilities/getReportingCurrencyISO';
import type { NarrativeForm } from '../../utils/schema';
import { useTimeseriesData } from './hooks/useTimeseriesData';
import type { PastAnnualRevenueEarnings } from '../../types';

export type Props = {
  company: NarrativeValuatorCompany;
  values: NarrativeForm;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  height?: number;
  yAxisOffsetTop?: number;
};

export const TimeseriesPlot = ({
  company,
  values,
  pastAnnualRevenueEarnings,
  height = 240,
  yAxisOffsetTop = Y_AXIS_OFFSET_TOP,
}: Props) => {
  const { ref: containerRef, width } = useResizeObserver();
  const chartWidth = width ?? 0;

  const tradingCurrencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) || 'XXX';
  const reportingCurrencyISO =
    getReportingCurrencyISO(company.data.currencyInfo) || 'XXX';

  let currencyISO = tradingCurrencyISO;
  if (tradingCurrencyISO !== reportingCurrencyISO) {
    currencyISO = reportingCurrencyISO;
  }

  const {
    timeRange,
    valueRange,
    pastTimeRange,
    futureTimeRange,
    plotData,
    mostRecentReportedYear,
  } = useTimeseriesData({
    profitMarginPercentage: values.profitMarginPercentage,
    revenuePercentage: values.revenuePercentage,
    timePeriod: values.timePeriod,
    pastAnnualRevenueEarnings,
  });

  const [hoveringYear, setHoveringYear] = useState<number>(
    futureTimeRange?.at(-1) ?? mostRecentReportedYear
  );

  useEffect(() => {
    /**
     * Show the projected year if any of the inputs change.
     */
    setHoveringYear(futureTimeRange?.at(-1) ?? mostRecentReportedYear);
  }, [
    values.timePeriod,
    values.fairValue,
    values.revenuePercentage,
    values.profitMarginPercentage,
    values.priceEarningsMultiple,
    values.discountRatePercentage,
    futureTimeRange,
    mostRecentReportedYear,
  ]);

  // Scaling Functions
  const { current: yScale } = useRef(scaleLinear<number>());

  // X Scale Config
  const [minYear, maxYear] = extent(timeRange);
  const { xScale, tickFormat, tickValues } = useXAxisConfig({
    chartWidth,
    currentYear: mostRecentReportedYear,
    minYear,
    maxYear,
  });

  yScale.range([height - Y_AXIS_OFFSET_BOTTOM, yAxisOffsetTop]);
  yScale.domain(extent(valueRange) as number[]);

  // Interaction Layer: hover function
  const handleMove = (
    e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>
  ) => {
    const point = localPoint(e);
    let x = 0;
    if (point !== null && point.x >= 0) {
      x = point.x;
    } else if (point !== null && point.x < 0) {
      const rect = (e.target as SVGRectElement).getBoundingClientRect();
      if ('touches' in e) {
        x = e.touches[0].clientX - rect.x;
      }
    }

    const scaledXCoordinate = x + LINE_PATH_STROKE;
    const year = xScale.invert(Math.round(scaledXCoordinate)).getFullYear();

    if (year !== hoveringYear && typeof plotData[year] !== 'undefined') {
      setHoveringYear(year);
    }
  };

  const hasPastTimeRange = pastTimeRange && pastTimeRange.length > 0;

  const latestPastYear = hasPastTimeRange ? pastTimeRange[0].toString() : null;
  const earliestPastYear = hasPastTimeRange
    ? (pastTimeRange.at(-1) as number).toString()
    : null;

  const xScaleLatestPastYear = latestPastYear
    ? xScale(new Date(latestPastYear))
    : null;
  const xScaleEarliestPastYear = earliestPastYear
    ? xScale(new Date(earliestPastYear))
    : null;
  const hundredPercent = '100%';
  const zeroPercent = '0%';
  const past = 'Past';
  const future = 'Future';

  return (
    <div ref={containerRef}>
      <svg width={chartWidth} height={height} className="overflow-visible">
        {/* Future and past rectangle */}
        <defs>
          <linearGradient
            id="past-gradient"
            x1={hundredPercent}
            y1={zeroPercent}
            x2={zeroPercent}
            y2={zeroPercent}
          >
            <stop
              offset={zeroPercent}
              className="[stop-color:var(--s-color-p-black-100)] [stop-opacity:0.2]"
            />
            <stop
              offset={hundredPercent}
              className="[stop-color:var(--s-color-p-black-100)] [stop-opacity:0]"
            />
          </linearGradient>
        </defs>
        {!!xScaleLatestPastYear && !!xScaleEarliestPastYear && (
          <>
            <Text
              x={xScaleLatestPastYear - xScaleEarliestPastYear + 12}
              y={yAxisOffsetTop + 16}
              className="fill-softer"
            >
              {past}
            </Text>
            <Text
              x={xScaleLatestPastYear + 12}
              y={yAxisOffsetTop + 16}
              className="fill-softer"
            >
              {future}
            </Text>
            <Bar
              x={xScaleEarliestPastYear}
              y={yAxisOffsetTop}
              width={Math.max(0, xScaleLatestPastYear - xScaleEarliestPastYear)}
              height={height - yAxisOffsetTop - Y_AXIS_OFFSET_BOTTOM}
              fill="url(#past-gradient)"
              style={{ pointerEvents: 'none' }}
            />
          </>
        )}

        {/* Interaction Layer: Hover Line */}
        {plotData[hoveringYear] && (
          <g transform={`translate(0, ${yAxisOffsetTop})`}>
            <line
              y1={0}
              y2={height - yAxisOffsetTop - Y_AXIS_OFFSET_BOTTOM}
              x1={xScale(new Date(hoveringYear.toString()))}
              x2={xScale(new Date(hoveringYear.toString()))}
              strokeWidth={1}
              strokeLinecap="round"
              strokeDasharray={4}
              className="stroke-p-white-170"
            />
          </g>
        )}

        {/* Axis Layer: Y Axis */}
        <g transform={`translate(${chartWidth - Y_AXIS_OFFSET_RIGHT}, 0)`}>
          <Axis
            orientation="right"
            stroke="var(--s-color-p-white-170)"
            tickStroke="var(--s-color-p-white-170)"
            tickLabelProps={() => ({
              fill: 'var(--s-color-text-softer)',
              fontSize: TICK_FONT_SIZE,
              textAnchor: 'start',
              dy: 3,
              dx: 2,
            })}
            tickFormat={(d) => toTruncated(d as number, 0)}
            tickValues={yScale.domain()}
            scale={yScale}
            tickLength={4}
            numTicks={1}
          />
        </g>

        {/* Axis Layer: X Axis */}
        <g transform={`translate(0, ${height - Y_AXIS_OFFSET_BOTTOM})`}>
          <Axis
            rangePadding={{
              start: X_AXIS_PADDING_LEFT,
              end: X_AXIS_PADDING_RIGHT,
            }}
            orientation="bottom"
            scale={xScale}
            stroke="var(--s-color-p-white-170)"
            tickFormat={(d) => tickFormat(d as Date)}
            tickLabelProps={() => ({
              fill: 'var(--s-color-text-softer)',
              fontSize: TICK_FONT_SIZE,
              textAnchor: 'middle',
              dy: 0,
              dx: 0,
            })}
            tickLength={4}
            tickValues={tickValues}
          />
        </g>

        {/* Line Layer: Line Markers */}
        <defs>
          <marker id="revenue-marker-future" overflow="visible">
            <g>
              <circle r={1} fill="none" className="stroke-p-white-100" />
              <circle r={1} className="fill-chart-01" />
            </g>
          </marker>
          <marker id="earnings-marker-future" overflow="visible">
            <g>
              <circle r={1} fill="none" className="stroke-p-white-100" />
              <circle r={1} className="fill-chart-02" />
            </g>
          </marker>
          <linearGradient
            id="revenue-line-gradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              className="[stop-color:var(--s-color-chart-01)] [stop-opacity:0.2]"
            />
            <stop
              offset="100%"
              className="[stop-color:var(--s-color-chart-01)] [stop-opacity:0]"
            />
          </linearGradient>
          <linearGradient
            id="earnings-line-gradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              className="[stop-color:var(--s-color-chart-02)] [stop-opacity:0.2]"
            />
            <stop
              offset="100%"
              className="[stop-color:var(--s-color-chart-02)] [stop-opacity:0]"
            />
          </linearGradient>
          <linearGradient
            id="negative-earnings-line-gradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              className="[stop-color:var(--s-color-bad)] [stop-opacity:0.6]"
            />
            <stop
              offset="100%"
              className="[stop-color:var(--s-color-bad)] [stop-opacity:0]"
            />
          </linearGradient>
        </defs>

        {/* Line Layer: Revenue Past */}
        <LinePath
          curve={curveMonotoneX}
          data={pastTimeRange}
          className="stroke-chart-01-softer"
          strokeWidth={2}
          strokeLinecap="round"
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.revenue ?? 0)}
        />

        {/* Line Layer: Revenue Future */}
        <LinePath
          curve={curveMonotoneX}
          data={futureTimeRange}
          className="stroke-chart-01"
          strokeWidth={2}
          strokeLinecap="round"
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.revenue ?? 0)}
          markerEnd="url(#revenue-marker-future)"
        />
        <AreaClosed
          curve={curveMonotoneX}
          data={timeRange}
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.revenue ?? 0)}
          yScale={yScale}
          fill={'url(#revenue-line-gradient)'}
          y0={() => height - Y_AXIS_OFFSET_BOTTOM}
        />

        <AreaClosed
          curve={curveMonotoneX}
          data={timeRange}
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.netIncome ?? 0)}
          yScale={yScale}
          fill={'url(#earnings-line-gradient)'}
          y0={() => height - Y_AXIS_OFFSET_BOTTOM}
        />

        <Threshold
          id="narrative-chart-threshold"
          data={timeRange}
          x={(d) => xScale(new Date(d.toString()))}
          y0={() => yScale(0)}
          y1={(d) => yScale(plotData[d]?.netIncome ?? 0)}
          clipAboveTo={0}
          clipBelowTo={height - Y_AXIS_OFFSET_BOTTOM}
          curve={curveMonotoneX}
          belowAreaProps={{
            fill: 'transparent',
          }}
          aboveAreaProps={{
            fill: 'url(#negative-earnings-line-gradient)',
          }}
        />

        {/* Line Layer: Earnings Past */}
        <LinePath
          curve={curveMonotoneX}
          data={pastTimeRange}
          className="stroke-chart-02-softer"
          strokeWidth={2}
          strokeLinecap="round"
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.netIncome ?? 0)}
        />
        {/* Line Layer: Earnings Future */}
        <LinePath
          curve={curveMonotoneX}
          data={futureTimeRange}
          className="stroke-chart-02"
          strokeWidth={2}
          strokeLinecap="round"
          x={(d) => xScale(new Date(d.toString()))}
          y={(d) => yScale(plotData[d]?.netIncome ?? 0)}
          markerEnd="url(#earnings-marker-future)"
        />

        {/* Interaction Layer: Revenue & Earnings Tooltip */}
        {plotData[hoveringYear] && (
          <TimeseriesTooltips
            currencyIso={currencyISO}
            netIncome={plotData[hoveringYear].netIncome}
            revenue={plotData[hoveringYear].revenue}
            year={hoveringYear}
            xScale={xScale}
            yScale={yScale}
            chartWidth={chartWidth}
          />
        )}

        {/* Interaction Layer: Hover Layer */}
        <g transform={`translate(${X_AXIS_OFFSET_LEFT}, ${yAxisOffsetTop})`}>
          <rect
            width={Math.max(
              0,
              chartWidth - X_AXIS_OFFSET_LEFT - X_AXIS_OFFSET_RIGHT
            )}
            height={Math.max(0, height - yAxisOffsetTop - Y_AXIS_OFFSET_BOTTOM)}
            fill="transparent"
            onMouseMove={handleMove}
            onTouchMove={handleMove}
          />
        </g>
      </svg>
    </div>
  );
};
